import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { MAGIC_INSIGHTS_CREDITS_FEATURE_NAME } from '../../../classes/magic-insights-credits';
import { CompanyService } from '../../../services/company.service';
import { DataSharingService } from '../../../services/data-sharing.service';
import { MessageService } from '../../../services/message.service';

declare var $: any;

export interface GetInsightsModule {
  id: number;
  name: string;
  isOrganisationModule: boolean;
  questionCount?: number;
}

export interface GetInsightsCreditCalculation {
  module: { id: number; name: string; type: 'ready_made' | 'custom'; question_count: number };
  company_count: number;
  ready_made_company_count: number;
  custom_research_answer_count: number;
  already_available_count: number;
  unavailable_company_count: number;
  credits_required: number;
  credits_remaining: number;
  has_enough_credits: boolean;
  timing: string;
}

@Component({
  selector: 'get-insights',
  templateUrl: './get-insights.component.html',
  styleUrl: './get-insights.component.css',
  standalone: false,
})
export class GetInsightsComponent implements OnInit, OnDestroy {
  @Input() modulesInfo: GetInsightsModule[] = [];
  @Input() numberOfCompaniesMatchingSearchParams: number = null;
  @Input() explicitCompanyIds: number[] = null;
  @Input() modalId = 'getInsightsModal';
  @Output() completed = new EventEmitter<any>();

  readonly featureName = MAGIC_INSIGHTS_CREDITS_FEATURE_NAME;
  readonly maximumCompanies = 200000;

  selectedModuleId: number = null;
  selectedCompanyLimit: number = null;
  companyIds: number[] = [];
  searchParams = new HttpParams();
  creditCalculation: GetInsightsCreditCalculation = null;
  requestId: string = null;
  showModulePicker = true;

  private subscriptions: Subscription[] = [];
  private creditCalculationRequestSequence = 0;

  constructor(
    private dataStore: DataSharingService,
    private companyService: CompanyService,
    private messageService: MessageService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.dataStore.currentSelectedCompanyIds.subscribe(ids => {
        this.companyIds = ids || [];
      }),
      this.dataStore.currentSelectedSearchParams.subscribe(params => {
        this.searchParams = params || new HttpParams();
      }),
      this.dataStore.currentMagicInsightsUnlockCount.subscribe(count => {
        this.selectedCompanyLimit = count;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  open(moduleId: number = null): void {
    this.selectedModuleId = moduleId ?? this.modulesInfo[0]?.id ?? null;
    this.showModulePicker = moduleId === null;
    this.requestId = this.newRequestId();
    if (this.selectedModuleId) {
      this.refreshCreditCalculation();
    }
    this.changeDetector.detectChanges();
    $(`#${this.modalId}`).modal('show');
  }

  get selectedModule(): GetInsightsModule | null {
    return this.modulesInfo.find(module => module.id === this.selectedModuleId) || null;
  }

  get selectedCompanyIds(): number[] {
    return this.explicitCompanyIds?.length ? this.explicitCompanyIds : this.companyIds;
  }

  get isManualSelection(): boolean {
    return this.selectedCompanyIds.length > 0;
  }

  get selectedCompanyCount(): number {
    if (this.isManualSelection) return this.selectedCompanyIds.length;
    return this.selectedCompanyLimit || this.numberOfCompaniesMatchingSearchParams || 0;
  }

  get maximumCreditCost(): number {
    return this.creditCalculation?.credits_required || 0;
  }

  get projectedCreditBalance(): number {
    if (!this.creditCalculation) return 0;
    return this.creditCalculation.credits_remaining - this.maximumCreditCost;
  }

  get hasReadyMadeModules(): boolean {
    return this.modulesInfo.some(module => !module.isOrganisationModule);
  }

  get hasCustomModules(): boolean {
    return this.modulesInfo.some(module => module.isOrganisationModule);
  }

  get actionableResultCount(): number {
    if (!this.creditCalculation || !this.selectedModule) return 0;
    return this.selectedModule.isOrganisationModule
      ? this.creditCalculation.custom_research_answer_count
      : this.creditCalculation.ready_made_company_count;
  }

  get hasActionableResults(): boolean {
    return this.actionableResultCount > 0;
  }

  get submitButtonLabel(): string {
    if (this.isSelectionTooLarge) return 'Reduce company count';
    if (!this.selectedModule) return 'Choose an insight';
    if (this.creditCalculation && !this.hasActionableResults) return 'Nothing available';
    if (this.creditCalculation && !this.creditCalculation.has_enough_credits) return 'Not enough credits';
    return 'Get insights';
  }

  displayModuleName(module: GetInsightsModule): string {
    return module?.name?.replace(/^Magic Insights\s*[-–—:]\s*/i, '') || '';
  }

  get isSelectionTooLarge(): boolean {
    return this.selectedCompanyCount > this.maximumCompanies;
  }

  get canSubmit(): boolean {
    return !!this.selectedModule && !!this.creditCalculation && this.hasActionableResults &&
      this.creditCalculation.has_enough_credits &&
      this.selectedCompanyCount > 0 && !this.isSelectionTooLarge && !this.isCalculating && !this.isSubmitting;
  }

  get isSubmitting(): boolean {
    return !!(
      this.companyService.isLoading.loadingUnlockMagicInsightsModules ||
      this.companyService.isLoading.loadingGenerateMagicInsights
    );
  }

  get isCalculating(): boolean {
    return !!this.companyService.isLoading.loadingCalculateMagicInsightsCredits;
  }

  get timingMessage(): string {
    if (this.creditCalculation?.timing) return this.creditCalculation.timing;
    if (!this.selectedModule?.isOrganisationModule) {
      return 'Ready-made insights are available immediately.';
    }
    if (this.selectedCompanyCount <= 1) {
      return 'Research normally completes within 5 minutes.';
    }
    if (this.selectedCompanyCount <= 100) {
      return 'Research normally completes within 30 minutes.';
    }
    return 'The first 100 companies start immediately; the remainder normally completes within 24 hours.';
  }

  moduleChanged(): void {
    this.requestId = this.newRequestId();
    this.refreshCreditCalculation();
  }

  refreshCreditCalculation(): void {
    const requestSequence = ++this.creditCalculationRequestSequence;
    if (!this.selectedModuleId || this.selectedCompanyCount < 1 || this.isSelectionTooLarge) {
      this.creditCalculation = null;
      return;
    }
    const requestedModuleId = this.selectedModuleId;
    this.creditCalculation = null;
    this.companyService.calculateMagicInsightsCredits(
      requestedModuleId,
      this.isManualSelection ? this.selectedCompanyIds : null,
      this.isManualSelection ? null : this.searchParams,
      this.isManualSelection ? null : this.selectedCompanyLimit,
    ).subscribe(calculation => {
      if (
        requestSequence !== this.creditCalculationRequestSequence ||
        requestedModuleId !== this.selectedModuleId ||
        Number(calculation?.module?.id) !== requestedModuleId
      ) return;
      if (calculation?.credits_required === undefined) return;
      this.creditCalculation = calculation;
      this.changeDetector.detectChanges();
    });
  }

  submit(): void {
    if (!this.canSubmit) return;

    const module = this.selectedModule;
    if (module.isOrganisationModule) {
      this.companyService.generateMagicInsights(
        module.id,
        this.searchParams,
        this.isManualSelection ? this.selectedCompanyIds : null,
        this.selectedCompanyLimit,
        this.numberOfCompaniesMatchingSearchParams,
        this.requestId,
        this.creditCalculation.credits_required,
      ).subscribe(response => {
        if (!response.body?.aira_run_id) return;
        this.finish(response, module);
        if (this.selectedCompanyCount !== 1) {
          this.messageService.showInfo(
            'Your Magic Insights research has been submitted. Results will appear automatically when ready.',
          );
        }
      });
      return;
    }

    this.companyService.unlockMagicInsightsModules(
      module.id,
      this.selectedCompanyIds,
      this.isManualSelection ? null : this.searchParams,
      this.selectedCompanyLimit,
      this.creditCalculation.credits_required,
      true,
    ).subscribe(response => {
      if (!response?.ok) return;
      this.finish(response, module);
      this.messageService.showInfo('Your ready-made Magic Insights are now available.', 4000);
    });
  }

  private finish(response: any, module: GetInsightsModule): void {
    $(`#${this.modalId}`).modal('hide');
    this.completed.emit({ response, moduleId: module.id, isOrganisationModule: module.isOrganisationModule });
  }

  private newRequestId(): string {
    return `get-insights-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}
